import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { MaterialFilters } from './MaterialFilters';
import { materials } from '../data/materials';
import { MaterialDialog } from './MaterialDialog';
import useEmblaCarousel from 'embla-carousel-react';
import { 
  Star, 
  Award, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Quote, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Truck,
  Wrench,
  Heart,
  Info,
  ArrowRight,
  Timer,
  Percent,
  BadgeCheck,
  Gem,
  Zap,
  Building2,
  Award as AwardIcon
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  },
  exit: { opacity: 0 }
};

const itemVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const cardVariants = {
  initial: { 
    opacity: 0,
    y: 40,
    scale: 0.9
  },
  animate: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const cardHoverVariants = {
  initial: { 
    scale: 1,
    y: 0,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
  },
  hover: { 
    scale: 1.02,
    y: -5,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  },
  tap: { 
    scale: 0.98,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  }
};

const imageHoverVariants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const buttonHoverVariants = {
  initial: { 
    scale: 1,
    backgroundColor: "rgb(180, 160, 108)" // gold
  },
  hover: { 
    scale: 1.05,
    backgroundColor: "rgb(139, 115, 85)", // gold-dark
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  },
  tap: { scale: 0.95 }
};

const glowVariants = {
  initial: { opacity: 0 },
  hover: { 
    opacity: [0.3, 0.5, 0.3],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "linear"
    }
  }
};

const getDiscount = (popularity: number): number => {
  if (popularity >= 95) {
    return 0.10;
  } else if (popularity >= 90) {
    return 0.05;
  }
  return 0;
};

const getProductionInfo = (popularity: number): { time: string; status: string; color: string } => {
  if (popularity >= 95) {
    return { time: '1-2 Weeks', status: 'High Demand', color: 'text-red-500' };
  } else if (popularity >= 90) {
    return { time: '2-3 Weeks', status: 'Standard', color: 'text-yellow-500' };
  } else {
    return { time: '1-2 Weeks', status: 'Available', color: 'text-green-500' };
  }
};

const testimonials = [
  {
    id: 1,
    name: 'Sarah Johnson',
    role: 'Interior Designer',
    company: 'Studio Elegance',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
    quote: 'The quality and customization options are exceptional. My clients are always impressed with the final result.',
    rating: 5,
    project: {
      type: 'Kitchen Renovation',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
      details: 'Custom marble countertop with integrated sink',
      completion: '2024',
      value: '€45,000'
    }
  },
  {
    id: 2,
    name: 'Michael Chen',
    role: 'Restaurant Owner',
    company: 'The Golden Plate',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
    quote: 'The durability and elegance of their surfaces have transformed our dining space. Excellent service from start to finish.',
    rating: 5,
    project: {
      type: 'Commercial Dining',
      image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
      details: 'Custom dining tables for 200-seat restaurant',
      completion: '2023',
      value: '€120,000'
    }
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    role: 'Luxury Home Developer',
    company: 'Elite Estates',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
    quote: 'Their attention to detail and premium materials have become a cornerstone of our luxury home offerings.',
    rating: 5,
    project: {
      type: 'Penthouse Suite',
      image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800',
      details: 'Complete surface package for luxury penthouse',
      completion: '2024',
      value: '€280,000'
    }
  }
];

export const MaterialSelection: React.FC = () => {
  const { setMaterial, setStep } = useCustomizationStore();
  const { currency, language } = useLocalizationStore();
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showInfo, setShowInfo] = useState<string | null>(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialRef, testimonialApi] = useEmblaCarousel({
    loop: true,
    align: 'center'
  });
  
  // Carousel for materials
  const [carouselRef, carouselApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    dragFree: true,
    containScroll: 'trimSnaps'
  });
  
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  useEffect(() => {
    if (testimonialApi) {
      const interval = setInterval(() => {
        testimonialApi.scrollNext();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonialApi]);
  
  // Update carousel navigation buttons state
  useEffect(() => {
    if (carouselApi) {
      const onSelect = () => {
        setCanScrollPrev(carouselApi.canScrollPrev());
        setCanScrollNext(carouselApi.canScrollNext());
      };
      
      carouselApi.on('select', onSelect);
      // Initialize
      onSelect();
      
      return () => {
        carouselApi.off('select', onSelect);
      };
    }
  }, [carouselApi]);

  const handleSelect = (material: Material) => {
    setMaterial(material);
    setStep('purchase-path');
  };
  
  const scrollPrev = () => carouselApi?.scrollPrev();
  const scrollNext = () => carouselApi?.scrollNext();

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="min-h-screen bg-[#1C1C1C] py-4 sm:py-8 lg:py-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-8 lg:mb-16">
          <motion.div
            variants={itemVariants}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gold mb-4 sm:mb-6">
              Premium Porcelain Surfaces
            </h2>
            <p className="text-lg sm:text-xl text-white/70">
              Transform your space with our exclusive collection of European porcelain. 
              Unmatched quality, timeless elegance.
            </p>
          </motion.div>
        </div>

        <MaterialFilters />

        {/* Materials Carousel */}
        <div className="relative mb-8">
          <div className="overflow-hidden" ref={carouselRef}>
            <div className="flex">
              {materials.map((material) => {
                const discount = getDiscount(material.popularity);
                const discountedPrice = material.price * (1 - discount);
                const discountedSlabPrice = discountedPrice * 5.12;
                const hasDiscount = discount > 0;
                const productionInfo = getProductionInfo(material.popularity);

                return (
                  <div key={material.id} className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 pl-4">
                    <motion.div
                      variants={cardHoverVariants}
                      initial="initial"
                      whileHover="hover"
                      whileTap="tap"
                      onClick={() => handleSelect(material)}
                      className="relative h-full"
                    >
                      <motion.div
                        variants={glowVariants}
                        className="absolute -inset-px rounded-xl bg-gradient-to-r from-gold/20 to-gold-dark/20 blur-sm"
                      />
                      
                      <div className="relative bg-[#1C1C1C] rounded-xl shadow-lg overflow-hidden cursor-pointer border border-gold/20 hover:border-gold/40 transition-colors duration-300 h-full flex flex-col">
                        <div className="relative h-48 sm:h-64 overflow-hidden">
                          <motion.img
                            variants={imageHoverVariants}
                            src={material.image}
                            alt={material.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          {hasDiscount && (
                            <motion.div
                              initial={{ x: -100 }}
                              animate={{ x: 0 }}
                              transition={{ type: "spring", stiffness: 300, damping: 25 }}
                              className="absolute top-4 left-4 bg-red-500 text-white px-2 sm:px-3 py-1 rounded-full flex items-center gap-1.5 text-sm"
                            >
                              <Percent className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="font-medium">{discount * 100}% OFF</span>
                            </motion.div>
                          )}
                          <div className="absolute top-4 right-4 flex flex-wrap gap-1 sm:gap-2 max-w-[calc(100%-2rem)]">
                            {material.finish.map(finish => (
                              <motion.span
                                key={finish}
                                whileHover={{ scale: 1.05 }}
                                className="px-2 sm:px-3 py-1 bg-black/50 backdrop-blur-sm text-white rounded-full text-xs sm:text-sm whitespace-nowrap hover:bg-black/70 transition-colors"
                              >
                                {finish}
                              </motion.span>
                            ))}
                          </div>
                        </div>

                        <div className="p-4 sm:p-6 flex flex-col flex-grow">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1 min-w-0 pr-4">
                              <h4 className="text-base sm:text-lg lg:text-xl font-semibold text-white mb-1 truncate">
                                {material.name}
                              </h4>
                              <p className="text-sm text-white/70 line-clamp-2">
                                {material.description}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Star className="w-4 h-4 text-gold fill-current" />
                              <span className="text-white/90">{material.popularity}%</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                            <div className="bg-white/5 rounded-lg p-3">
                              <div className="space-y-2">
                                <div>
                                  <span className="text-white/60 text-xs">Price per m²</span>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {hasDiscount && (
                                      <span className="text-xs sm:text-sm line-through text-white/40">
                                        {formatCurrency(material.price, currency, language)}
                                      </span>
                                    )}
                                    <p className="text-base sm:text-lg lg:text-xl font-bold text-gold">
                                      {formatCurrency(discountedPrice, currency, language)}
                                    </p>
                                  </div>
                                </div>
                                <div>
                                  <span className="text-white/60 text-xs">Total Slab</span>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {hasDiscount && (
                                      <span className="text-xs line-through text-white/40">
                                        {formatCurrency(material.price * 5.12, currency, language)}
                                      </span>
                                    )}
                                    <p className="text-sm font-bold text-gold/80">
                                      {formatCurrency(discountedSlabPrice, currency, language)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3">
                              <span className="text-white/60 text-xs">Production Time</span>
                              <div className="flex items-center gap-2">
                                <Timer className={`w-4 h-4 ${productionInfo.color}`} />
                                <div>
                                  <p className="text-sm font-medium text-white">
                                    {productionInfo.time}
                                  </p>
                                  <p className={`text-xs ${productionInfo.color}`}>
                                    {productionInfo.status}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-auto flex items-center justify-between">
                            <div className="hidden sm:flex items-center gap-4 text-white/60">
                              <motion.div 
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center gap-1"
                              >
                                <Shield className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">10yr warranty</span>
                              </motion.div>
                              <motion.div 
                                whileHover={{ scale: 1.05 }}
                                className="flex items-center gap-1"
                              >
                                <Truck className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">Free delivery</span>
                              </motion.div>
                            </div>
                            <motion.button
                              variants={buttonHoverVariants}
                              className="bg-gold text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm sm:text-base ml-auto"
                            >
                              Select Material
                              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Carousel Navigation */}
          <div className="flex justify-center mt-6 gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className={`p-3 rounded-full ${
                canScrollPrev 
                  ? 'bg-white/10 text-white hover:bg-white/20' 
                  : 'bg-white/5 text-white/30 cursor-not-allowed'
              } transition-colors`}
            >
              <ChevronLeft className="w-5 h-5" />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollNext}
              disabled={!canScrollNext}
              className={`p-3 rounded-full ${
                canScrollNext 
                  ? 'bg-white/10 text-white hover:bg-white/20' 
                  : 'bg-white/5 text-white/30 cursor-not-allowed'
              } transition-colors`}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        <motion.div 
          variants={itemVariants}
          className="mt-8 sm:mt-16 text-center"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-gold/80 mb-6 sm:mb-8">
            Why Choose Our Premium Surfaces
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 shadow-md border border-gold/20">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h4 className="font-semibold text-gold mb-2">Quality Guaranteed</h4>
              <p className="text-xs sm:text-sm text-white/70">Premium European materials with extended warranty</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 shadow-md border border-gold/20">
              <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h4 className="font-semibold text-gold mb-2">Professional Delivery</h4>
              <p className="text-xs sm:text-sm text-white/70">White-glove delivery service included</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 shadow-md border border-gold/20">
              <Wrench className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h4 className="font-semibold text-gold mb-2">Expert Installation</h4>
              <p className="text-xs sm:text-sm text-white/70">Certified installation team</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 shadow-md border border-gold/20">
              <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h4 className="font-semibold text-gold mb-2">Lifetime Support</h4>
              <p className="text-xs sm:text-sm text-white/70">Dedicated customer service</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="mt-16 mb-16 bg-gradient-to-br from-gold/10 to-gold-dark/10 rounded-2xl p-8 sm:p-12 relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 2px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="text-center mb-12 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-gold/10 text-gold px-6 py-3 rounded-full mb-8"
            >
              <AwardIcon className="w-5 h-5" />
              <span className="font-medium">Client Success Stories</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-white mb-6"
            >
              Transforming Visions into Reality
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-white/70 max-w-3xl mx-auto"
            >
              See how our premium surfaces have elevated spaces across luxury homes, high-end restaurants, and exclusive commercial projects.
            </motion.p>
          </div>

          <div className="relative">
            <div className="overflow-hidden" ref={testimonialRef}>
              <div className="flex">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="flex-[0_0_100%] min-w-0">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-gold/5 group-hover:opacity-75 transition-opacity duration-500" />
                        <img
                          src={testimonial.project.image}
                          alt={testimonial.project.type}
                          className="w-full h-[600px] object-cover rounded-2xl"
                        />
                        <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-sm p-6 rounded-xl">
                          <h4 className="text-white font-semibold mb-2">{testimonial.project.type}</h4>
                          <p className="text-white/80 text-sm">{testimonial.project.details}</p>
                          <div className="flex items-center gap-6 mt-4 text-sm text-white/60">
                            <span>Completed: {testimonial.project.completion}</span>
                            <span>Value: {testimonial.project.value}</span>
                          </div>
                        </div>
                      </div>

                      <div className="relative">
                        <Quote className="absolute -top-6 -left-6 w-12 h-12 text-gold/20" />
                        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                          <div className="flex items-center gap-4 mb-8">
                            <img
                              src={testimonial.image}
                              alt={testimonial.name}
                              className="w-16 h-16 rounded-full object-cover ring-4 ring-gold/20"
                            />
                            <div>
                              <h4 className="text-white font-semibold text-xl">{testimonial.name}</h4>
                              <p className="text-gold">{testimonial.role}</p>
                              <div className="flex items-center gap-2 text-white/60">
                                <Building2 className="w-4 h-4" />
                                <p className="text-sm">{testimonial.company}</p>
                              </div>
                            </div>
                          </div>

                          <blockquote className="text-white/90 text-xl font-light italic mb-8 leading-relaxed">
                            "{testimonial.quote}"
                          </blockquote>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star
                                  key={i}
                                  className="w-5 h-5 text-gold fill-current"
                                />
                              ))}
                            </div>
                            <div className="flex items-center gap-2 text-white/60">
                              <BadgeCheck className="w-5 h-5 text-gold" />
                              <span>Verified Project</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center items-center gap-8 mt-12">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => testimonialApi?.scrollPrev()}
                className="p-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </motion.button>

              <div className="flex items-center gap-3">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => testimonialApi?.scrollTo(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      testimonialApi?.selectedScrollSnap() === index
                        ? 'bg-gold w-12'
                        : 'bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => testimonialApi?.scrollNext()}
                className="p-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>

      <MaterialDialog
        show={showMaterialDialog}
        onClose={() => setShowMaterialDialog(false)}
        onSelect={handleSelect}
        selectedMaterial={null}
        materials={materials}
      />
    </motion.div>
  );
};

export default MaterialSelection;